import {
  captureConversationAccess,
  conversationRevocation,
  listedConversationRecords,
} from 'dashboard/permissionSets/conversationReads';
import * as types from '../mutation-types';
import ContactAPI from '../../api/contacts';
import ConversationApi from '../../api/conversations';
import camelcaseKeys from 'camelcase-keys';

const contactReads = new Map();

export const createMessagePayload = (payload, message) => {
  const { content, cc_emails, bcc_emails } = message;
  payload.append('message[content]', content);
  if (cc_emails) payload.append('message[cc_emails]', cc_emails);
  if (bcc_emails) payload.append('message[bcc_emails]', bcc_emails);
};

export const createConversationPayload = ({ params, contactId, files }) => {
  const { inboxId, message, sourceId, mailSubject, assigneeId } = params;
  const payload = new FormData();

  if (message) {
    createMessagePayload(payload, message);
  }

  if (files && files.length > 0) {
    files.forEach(file => payload.append('message[attachments][]', file));
  }

  payload.append('inbox_id', inboxId);
  payload.append('contact_id', contactId);
  payload.append('source_id', sourceId);
  if (mailSubject) {
    payload.append('additional_attributes[mail_subject]', mailSubject);
  }
  payload.append('assignee_id', assigneeId);

  return payload;
};

export const createWhatsAppConversationPayload = ({ params }) => {
  const { inboxId, message, contactId, sourceId, assigneeId } = params;

  const payload = {
    inbox_id: inboxId,
    contact_id: contactId,
    source_id: sourceId,
    message,
    assignee_id: assigneeId,
  };

  return payload;
};

const setNewConversationPayload = ({
  isFromWhatsApp,
  params,
  contactId,
  files,
}) => {
  if (isFromWhatsApp) {
    return createWhatsAppConversationPayload({ params });
  }
  return createConversationPayload({
    params,
    contactId,
    files,
  });
};

const state = {
  records: {},
  neighbours: {},
  uiFlags: {
    isFetching: false,
  },
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getContactConversation: $state => id => {
    const records = $state.records[Number(id)] || [];
    return [...records].sort(
      (a, b) => b.created_at - a.created_at || b.id - a.id
    );
  },
  getAllConversationsByContactId: $state => id => {
    const records = $state.records[Number(id)] || [];
    return camelcaseKeys(records, { deep: true });
  },
  getConversationNeighbours: $state => conversationId =>
    $state.neighbours[Number(conversationId)] || [],
};

export const actions = {
  create: async (context, { params, isFromWhatsApp }) => {
    const { commit } = context;
    const isCurrent = captureConversationAccess(context);
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isCreating: true,
    });
    const { contactId, files } = params;
    try {
      const payload = setNewConversationPayload({
        isFromWhatsApp,
        params,
        contactId,
        files,
      });

      const { data } = await ConversationApi.create(payload);
      // Access to the new conversation may be lost before the response.
      if (
        context.state.records[contactId] &&
        isCurrent() &&
        !conversationRevocation(context.rootState, data.id)
      ) {
        commit(types.default.ADD_CONTACT_CONVERSATION, { id: contactId, data });
      }

      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      if (isCurrent()) {
        commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
          isCreating: false,
        });
      }
    }
  },
  get: async (context, contactId) => {
    const { commit } = context;
    const accessIsCurrent = captureConversationAccess(context);
    // Only the newest history read of a contact applies its response.
    // Routes pass string ids and conversations numeric ones: one key per contact.
    const key = Number(contactId);
    const read = {};
    contactReads.set(key, read);
    const isCurrent = () => accessIsCurrent() && contactReads.get(key) === read;
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isFetching: true,
    });
    try {
      const response = await ContactAPI.getConversations(contactId);
      if (!isCurrent()) return;
      commit(types.default.SET_CONTACT_CONVERSATIONS, {
        id: contactId,
        data: listedConversationRecords(
          context.rootState,
          response.data.payload,
          conversation => conversation.id
        ),
      });
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
    } catch (error) {
      if (!isCurrent()) return;
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  getNeighbours: async (context, { contactId, conversationId }) => {
    const { commit } = context;
    const isCurrent = captureConversationAccess(context, conversationId);
    try {
      const { data } = await ContactAPI.getConversations(contactId, {
        conversationId,
      });
      if (!isCurrent()) return;
      commit(types.default.SET_CONVERSATION_NEIGHBOURS, {
        id: conversationId,
        data: listedConversationRecords(
          context.rootState,
          data.payload,
          conversation => conversation.id
        ),
      });
    } catch (error) {
      // The links stay hidden when the neighbours cannot be resolved.
    }
  },
};

export const mutations = {
  [types.default.CLEAR_CONVERSATION_ACCESS]($state) {
    $state.records = {};
    $state.neighbours = {};
    $state.uiFlags = { isFetching: false, isCreating: false };
  },
  [types.default.REVOKE_CONVERSATION_ACCESS]($state, conversationId) {
    $state.neighbours = Object.fromEntries(
      Object.entries($state.neighbours)
        .filter(([id]) => Number(id) !== conversationId)
        .map(([id, records]) => [
          id,
          records.filter(record => record.id !== conversationId),
        ])
    );
    $state.records = Object.fromEntries(
      Object.entries($state.records).map(([contactId, conversations]) => [
        contactId,
        conversations.filter(
          conversation => conversation.id !== conversationId
        ),
      ])
    );
  },
  [types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONTACT_CONVERSATIONS]: ($state, { id, data }) => {
    $state.records = {
      ...$state.records,
      [id]: data,
    };
  },
  [types.default.ADD_CONTACT_CONVERSATION]: ($state, { id, data }) => {
    const conversations = $state.records[id] || [];

    const updatedConversations = [...conversations];
    const index = conversations.findIndex(
      conversation => conversation.id === data.id
    );

    if (index !== -1) {
      updatedConversations[index] = { ...conversations[index], ...data };
    } else {
      updatedConversations.push(data);
    }

    $state.records = {
      ...$state.records,
      [id]: updatedConversations,
    };
  },
  [types.default.DELETE_CONTACT_CONVERSATION]: ($state, id) => {
    const { [id]: deletedRecord, ...remainingRecords } = $state.records;
    $state.records = remainingRecords;
  },
  [types.default.SET_CONVERSATION_NEIGHBOURS]: ($state, { id, data }) => {
    $state.neighbours = {
      ...$state.neighbours,
      [id]: data,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
